import { xray } from 'lib/xray';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatureQuotaCleanerGroupHints, getFeatureQuotaCleanerGroupTitles } from 'reactApp/modules/features/features.selectors';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { routerStart } from 'reactApp/modules/router/router.module';
import { startSelecting } from 'reactApp/modules/selections/selections.actions';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { hasMailSubscription } from 'reactApp/modules/subscriptions/subscriptions.selectors';
import { setShowUploaderAction } from 'reactApp/modules/uploadList/uploadList.module';
import { widgetTitles } from 'reactApp/modules/userQuotaCleaner/helpers/constants';
import { getCurrentGroup, getCurrentGroupTotalCount } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { sendQuotaCleanerGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { UserQuotaGroupId, UserQuotaGroupType } from 'reactApp/types/QuotaCleaner';
import { ActionsBar, EActionsMode } from 'reactApp/ui/Mobile/ActionsBar/ActionsBar';
import { Datalist } from 'reactApp/ui/Mobile/Datalist/Datalist';
import { Toolbar } from 'reactApp/ui/Mobile/Toolbar/Toolbar';
import { Header } from 'reactApp/ui/QuotaCleanerPage/Header/Header';
import { useCloseCleanerPage } from 'reactApp/ui/QuotaCleanerPage/hooks/useCloseCleanerPage';
import { QuotaCleanerPageMobileProps } from 'reactApp/ui/QuotaCleanerPage/quotaCleanerPage.toolkit';
import { QuotaCleanerSpacePromo } from 'reactApp/ui/QuotaCleanerSpacePromo/QuotaCleanerSpacePromo';
import { isIosDevice, scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';

import { optionHeaderText, url } from '../QuotaCleanerPage.data';
import styles from './QuotaCleanerPageMobile.css';

export const QuotaCleanerPageMobile: FC<QuotaCleanerPageMobileProps> = ({ isIOSPromo, isAndroidPromo, isMobilePayAvailable }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [scrollRef, setScrollRef] = useState(null);
    const updateScrollRef = useCallback((node) => {
        setScrollRef(node);
    }, []);

    const dispatch = useDispatch();
    const currentGroup = useSelector(getCurrentGroup);
    const isPaidUser = useSelector(hasMailSubscription);
    const totalCount = useSelector(getCurrentGroupTotalCount);
    const priorityGroupTitles = useSelector(getFeatureQuotaCleanerGroupTitles);
    const priorityGroupHints = useSelector(getFeatureQuotaCleanerGroupHints);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const isOldInbox = currentGroup?.groupId === UserQuotaGroupId.OldInbox;
    const showToolbarOptions = currentGroup?.groupType !== UserQuotaGroupType.Letter;

    const selectedIds = useSelector(SelectionsSelectors.getSelectedIdxs);
    const showActionsBar = selectedIds.length > 0;
    const { onClose } = useCloseCleanerPage();

    useEffect(() => xray.send('quota-cln-page-show-mob'), []);

    useEffect(() => {
        dispatch(setShowUploaderAction(false));
        dispatch(startSelecting());
    }, [dispatch]);

    useEffect(() => {
        if (!currentGroup?.isLoaded) {
            return;
        }

        const { groupId, size } = currentGroup || {};
        xray.send('quota-cln-curr-grp-mob', { i: groupId });
        sendQuotaCleanerGa({
            action: 'cleaner_show_dtld',
            dwh: { name_block: groupId, size_block: size, count_block: totalCount },
        });
    }, [currentGroup?.isLoaded]);

    useEffect(() => {
        dispatch(
            routerStart({
                id: url,
                params: {},
                storage: EStorageType.quotaCleaner,
                url,
                __isFolder: false,
                __parent: '/',
            })
        );
    }, [dispatch]);

    // to disable scroll of background elements
    useEffect(() => {
        if (!isIosDevice()) {
            const el = ref?.current;
            scrollLock(el);
            return () => scrollUnlock(el);
        }
    }, [ref]);

    return (
        <div ref={ref}>
            {currentGroup && (
                <div className={styles.wrapper} ref={updateScrollRef} data-qa-id="quota-cleaner-page">
                    <Toolbar
                        showSearch={false}
                        hideLogo={true}
                        showCloseArrow={true}
                        onCloseArrow={onClose}
                        showQuotaCleanerSelecting={true}
                        optionHeaderText={optionHeaderText}
                        isItemInfoHidden={true}
                        showOptions={showToolbarOptions}
                    />
                    <div data-promo-target="quota-cleaner-toolbar" />
                    {!isPaidUser && (
                        <QuotaCleanerSpacePromo
                            isIOSPromo={isIOSPromo}
                            isAndroidPromo={isAndroidPromo}
                            isMobilePayAvailable={isMobilePayAvailable}
                        />
                    )}
                    <div className={styles.list}>
                        <Header
                            title={
                                isOldInbox && isMobile
                                    ? 'Входящие письма'
                                    : priorityGroupTitles[currentGroup.groupId] || widgetTitles[currentGroup.groupId]
                            }
                            hint={priorityGroupHints[currentGroup.groupId]}
                            size={currentGroup.size ?? 0}
                        />
                        <Datalist customScrollElement={scrollRef} />
                    </div>
                    {showActionsBar && <ActionsBar mode={EActionsMode.SECONDARY} isPopup={true} />}
                </div>
            )}
        </div>
    );
};
